// The client half of the session flight recorder (SPEC §12).
//
// The server's trace has every input the turn manager folded and every stage
// the assistant stamped, but it cannot see the browser: when audio actually
// reached the speaker, what the microphone's noise floor was, which way ICE
// went, how far the client's clock is from the node's. Those are the
// observations that decide whether a turn that *looks* clean in the trace was
// clean in the room, so they are batched up here and written to the same file.
//
// Two rules shape this file. Events are opaque to the server — it stores the
// object verbatim — so the schema lives entirely on this side and a new kind
// costs no deployment. And the collector never queues across a socket gap:
// one batch is the whole buffer, a flush that finds the socket closed drops it
// and counts the loss, and the count rides on the next batch that lands. A
// telemetry backlog replayed minutes later would be indistinguishable from
// fresh observation, which is worse than a hole with a number beside it.
//
// A *suspend* is not a gap, it is the end of the file. The node closes the
// session's trace on `session.suspended` and a resume is a new session with a
// new trace, so the collector goes quiet there — `close()` — rather than
// holding a batch for a file that no longer exists. It must: the released
// connection answers `protocol_error` to anything but `session.start`, that
// error is itself an observation, and recording it would flush another batch a
// second later, for as long as the session stayed paused.

import type { ClientTelemetry, TelemetryEvent } from "./protocol";

/** Events per batch. Reached first on a busy session; 32 keeps a frame small. */
export const TELEMETRY_BATCH_MAX = 32;
/** Batch deadline: the longest an observation waits for company. */
export const TELEMETRY_BATCH_MS = 1_000;
/** `clock` and `stats` cadence. */
export const TELEMETRY_TICK_MS = 5_000;
/** `level` cadence: one aggregate of the worklet's own reports per second. */
export const TELEMETRY_LEVEL_MS = 1_000;
/**
 * Playout poll, while the liaison is speaking.
 *
 * What this resolves is the client's own buffer — the server truncates on its
 * own playout position, and the difference between the two is what a barge-in
 * argument turns on — and a live session measured that at a ~700 ms lead-in
 * and ~1.1 s of drain. 200 ms reads both to better than 20% and costs half of
 * what 100 ms did: `getStats()` is not free on the main thread (0.32 ms per
 * call measured on a desktop M4 with a 14-entry report — 0.03 ms to issue,
 * 0.23 ms to resolve, 0.10 ms to fold, p99 2.2 ms), and a phone-class device
 * is several times that, ten times a second, for the whole of every answer,
 * on the same thread as the consumer's transcript and meter.
 *
 * It also reads *better*: the energy is a mean square over the whole interval,
 * so a longer window averages a sentence pause in with the speech around it
 * rather than reporting it as silence.
 */
export const TELEMETRY_PLAYOUT_MS = 200;

/**
 * What `setTimeout` hands back, which has no portable name: `number` under the
 * DOM lib, `Timer` under Bun. The two only ever meet `clearTimeout`. Exported
 * because `index.ts` holds the same handles and one spelling of it is enough.
 */
export type Timer = ReturnType<typeof setTimeout>;

/**
 * Writes a batch, reporting whether it reached the wire. False means the
 * control socket is not open and this batch is lost.
 */
export type TelemetryWriter = (f: ClientTelemetry) => boolean;

/**
 * Batches client observations and hands them to the socket. Timers are its
 * own; the session owns when it is stopped, which is every path out of a
 * session — `end()`, a suspend, a teardown.
 */
export class Telemetry {
	private batch: TelemetryEvent[] = [];
	private timer: Timer | null = null;
	/** Events lost to a closed socket since the last batch that landed. */
	private lost = 0;
	/**
	 * `on` while a session is recording, `off` between a suspend and the next
	 * handshake — there is no trace to write to then — and `over` once the
	 * session has ended, which nothing reopens.
	 */
	private mode: "on" | "off" | "over" = "on";

	constructor(private readonly write: TelemetryWriter) {}

	/**
	 * Records one observation. `atMs` is stamped here rather than by the
	 * caller: every kind wants the same clock, and the server's own receive
	 * time beside it is what makes the offset derivable.
	 */
	record(kind: string, fields?: Record<string, unknown>): void {
		if (this.mode !== "on") return;
		this.batch.push({ ...fields, kind, atMs: Date.now() });
		if (this.batch.length >= TELEMETRY_BATCH_MAX) {
			this.flush();
			return;
		}
		this.timer ??= setTimeout(() => {
			this.timer = null;
			this.flush();
		}, TELEMETRY_BATCH_MS);
	}

	/**
	 * Sends what is held, if anything. A batch the socket could not carry is
	 * dropped and counted — see the header — and the count is reported as a
	 * `dropped` event on the next batch that lands, so a hole in the trace is
	 * never silent.
	 */
	flush(): void {
		this.clearTimer();
		const events = this.batch;
		if (events.length === 0) return;
		this.batch = [];
		const lost = this.lost;
		if (lost > 0) events.push({ kind: "dropped", atMs: Date.now(), events: lost });
		if (this.write({ t: "client.telemetry", events })) {
			this.lost = 0;
			return;
		}
		// The `dropped` marker itself is not an observation worth counting: it
		// is regenerated from the count on the next attempt.
		this.lost = lost + events.length - (lost > 0 ? 1 : 0);
	}

	/**
	 * Drops what is held and stops recording until `open()`. This is the
	 * suspend: the session's trace is closed on the node the moment
	 * `session.suspended` goes out, so the held batch has no file to land in —
	 * and the loss count goes with it, because the next trace is a different
	 * session's and must not open with a hole that belongs to this one. What
	 * was worth keeping went out in the flush `suspend()` makes while the
	 * session is still bound.
	 */
	close(): void {
		if (this.mode === "over") return;
		this.mode = "off";
		this.clearTimer();
		this.batch = [];
		this.lost = 0;
	}

	/** Records again, for the next session's trace. Never reopens a `stop()`. */
	open(): void {
		if (this.mode === "off") this.mode = "on";
	}

	/**
	 * Final flush, then silence for good. Anything recorded afterwards is
	 * discarded rather than buffered: the session it described is over, and a
	 * timer that outlives it is a leak that keeps the whole session reachable.
	 */
	stop(): void {
		this.flush();
		this.close();
		this.mode = "over";
	}

	private clearTimer(): void {
		if (this.timer === null) return;
		clearTimeout(this.timer);
		this.timer = null;
	}

}
